import { token } from '@atlaskit/tokens';
import {
  type HighlightHandle,
  type HighlightRange,
  type HighlightsRenderer
} from '@databases/highlights';
import { getService } from '@databases/service-container';
import { useEffect } from 'react';

import { useIsAiSummaryFieldEnabled } from './use-is-ai-summary-field-enabled.ts';

const COLUMN_RESIZE_EVENT_NAME = 'database-column-resize';
const TABLE_ID = 'layout-table';
const BOTTOM_CONTROLS_ID = 'add-entry-quick-calc-container';
const ADD_FIELD_BUTTON_ID = 'add-field-button';
const HIGHLIGHT_ID = 'ai-summary-column-streaming-highlight';
const HIGHLIGHT_CLASS_NAME = 'ai-summary-column-streaming-highlight';
const HEADER_HIGHLIGHT_CLASS_NAME = 'ai-summary-column-streaming-header-highlight';
const ADD_FIELD_HIGHLIGHT_CLASS_NAME = 'ai-summary-column-streaming-add-field-highlight';
const HIGHLIGHT_Z_INDEX = 1;
const HEADER_HIGHLIGHT_Z_INDEX = 2;
const HIGHLIGHT_BORDER_WIDTH_PX = 2;
const GRADIENT_ROTATION_DURATION_MS = 6_000;
const GRADIENT_DEGREES_PER_MS = 360 / GRADIENT_ROTATION_DURATION_MS;
// @atlaskit/tokens 8.5.0 does not expose the newer color.rovo.border.* tokens.
// These values intentionally match the existing AIFC border palette without
// coupling the AI fields package to the AIFC UI package.
const BORDER_ORANGE = '#FCA700';
const BORDER_GREEN = '#6A9A23';
const BORDER_BLUE = '#1868DB';
const BORDER_PURPLE = '#AF59E0';
const GRADIENT = `conic-gradient(
  from var(--database-ai-summary-gradient-angle, 0deg),
  ${BORDER_ORANGE} 25%,
  ${BORDER_GREEN} 25% 50%,
  ${BORDER_BLUE} 50% 75%,
  ${BORDER_PURPLE} 75% 100%
)`;
const GRADIENT_BORDER = `${GRADIENT} 1`;
const BORDER_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)';

export interface UseAiSummaryColumnStreamingHighlightOptions {
  fieldIds: readonly string[];
  streamingFieldId: string | undefined;
}

const findFieldHeader = (
  container: HTMLElement,
  fieldId: string
): HTMLTableCellElement | null => {
  const headers = container.querySelectorAll<HTMLTableCellElement>(
    'th[data-field-id][data-col-index]'
  );

  return Array.from(headers).find((header) => header.dataset.fieldId === fieldId) ?? null;
};

const addColumnHighlight = (
  layer: HighlightsRenderer,
  columnIndex: number
): { handle: HighlightHandle; range: HighlightRange } => {
  const range: HighlightRange = {
    id: HIGHLIGHT_ID,
    anchor: { row: 0, col: columnIndex },
    lead: { row: 0, col: columnIndex }
  };
  const handle = layer.add({
    range,
    mode: 'column',
    zIndex: HIGHLIGHT_Z_INDEX,
    className: HIGHLIGHT_CLASS_NAME
  });
  return { handle, range };
};

const getVisibleColumnRight = (
  container: HTMLElement,
  header: HTMLTableCellElement
): number => {
  const addFieldButton = container.querySelector<HTMLElement>(`#${ADD_FIELD_BUTTON_ID}`);
  const headerRight = header.getBoundingClientRect().right;

  return Math.min(
    headerRight,
    addFieldButton?.getBoundingClientRect().right ?? headerRight
  );
};

const applyColumnViewportClip = (
  container: HTMLElement,
  table: HTMLTableElement,
  header: HTMLTableCellElement,
  highlight: HTMLElement,
  bottomControls: HTMLElement | null
): void => {
  const containerRect = container.getBoundingClientRect();
  const tableRect = table.getBoundingClientRect();
  const headerRect = header.getBoundingClientRect();
  const highlightRect = highlight.getBoundingClientRect();
  const visibleRight = getVisibleColumnRight(container, header);
  const viewportBottom = containerRect.top + container.clientHeight;
  const controlsTop =
    bottomControls?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
  const visibleTop = Math.max(containerRect.top, headerRect.bottom);
  const visibleBottom = Math.max(
    visibleTop,
    Math.min(viewportBottom, tableRect.bottom, controlsTop)
  );
  const highlightHeight = Math.max(0, highlightRect.height);
  const topInset = Math.min(highlightHeight, Math.max(0, visibleTop - highlightRect.top));
  const bottomInset = Math.min(
    highlightHeight - topInset,
    Math.max(0, highlightRect.bottom - visibleBottom)
  );

  highlight.style.width = `${Math.max(0, visibleRight - highlightRect.left)}px`;
  highlight.style.clipPath = `inset(${topInset}px 0 ${bottomInset}px 0)`;
};

const createBorderHighlight = (className: string): HTMLDivElement => {
  const highlight = document.createElement('div');
  highlight.className = className;
  highlight.setAttribute('aria-hidden', 'true');
  highlight.style.position = 'absolute';
  highlight.style.inset = '0';
  highlight.style.boxSizing = 'border-box';
  highlight.style.padding = `${HIGHLIGHT_BORDER_WIDTH_PX}px`;
  highlight.style.background = GRADIENT;
  highlight.style.setProperty('-webkit-mask', BORDER_MASK);
  highlight.style.setProperty('-webkit-mask-composite', 'xor');
  highlight.style.setProperty('mask', BORDER_MASK);
  highlight.style.setProperty('mask-composite', 'exclude');
  highlight.style.pointerEvents = 'none';
  highlight.style.zIndex = HEADER_HIGHLIGHT_Z_INDEX.toString();

  return highlight;
};

const applyHeaderHighlight = (
  container: HTMLElement,
  header: HTMLTableCellElement,
  includeAddFieldHighlight: boolean
) => {
  const previousStyles = {
    borderBottom: header.style.borderBottom,
    boxShadow: header.style.boxShadow
  };
  const headerHighlight = createBorderHighlight(HEADER_HIGHLIGHT_CLASS_NAME);
  headerHighlight.style.borderRadius = 'inherit';
  const addFieldButton = includeAddFieldHighlight
    ? container.querySelector<HTMLElement>(`#${ADD_FIELD_BUTTON_ID}`)
    : null;
  const addFieldHeader = addFieldButton?.closest<HTMLTableCellElement>('th') ?? null;
  const addFieldHighlight = addFieldHeader
    ? createBorderHighlight(ADD_FIELD_HIGHLIGHT_CLASS_NAME)
    : null;
  if (addFieldHighlight) {
    addFieldHeader?.appendChild(addFieldHighlight);
  }

  header.dataset.aiSummaryColumnStreaming = 'true';
  header.style.borderBottom = '0';
  header.style.boxShadow = `inset 0 calc(-1 * ${token(
    'border.width',
    '1px'
  )}) 0 ${token('color.border', '#091E4224')}`;
  header.appendChild(headerHighlight);

  const updateBounds = () => {
    const headerRect = header.getBoundingClientRect();
    const headerStyles = getComputedStyle(header);
    const topLeftRadius = headerStyles.borderTopLeftRadius || '0px';
    const topRightRadius = headerStyles.borderTopRightRadius || '0px';
    const borderTopWidth = Number.parseFloat(headerStyles.borderTopWidth) || 0;
    const borderRightWidth = Number.parseFloat(headerStyles.borderRightWidth) || 0;
    const borderLeftWidth = Number.parseFloat(headerStyles.borderLeftWidth) || 0;
    const visibleRight = getVisibleColumnRight(container, header);
    const rightInset = Math.min(
      headerRect.width,
      Math.max(-borderRightWidth, headerRect.right - visibleRight - borderRightWidth)
    );
    headerHighlight.style.top = `${-borderTopWidth}px`;
    headerHighlight.style.left = `${-borderLeftWidth}px`;
    headerHighlight.style.right = `${rightInset}px`;
    headerHighlight.style.bottom = `-${HIGHLIGHT_BORDER_WIDTH_PX}px`;
    headerHighlight.style.clipPath =
      `inset(0 0 ${HIGHLIGHT_BORDER_WIDTH_PX}px 0 round ` +
      `${topLeftRadius} ${topRightRadius} 0 0)`;

    if (addFieldHighlight && addFieldButton && addFieldHeader) {
      const addFieldButtonRect = addFieldButton.getBoundingClientRect();
      const addFieldHeaderRect = addFieldHeader.getBoundingClientRect();
      const visibleRight = getVisibleColumnRight(container, header);
      addFieldHighlight.style.top = `${headerRect.top - addFieldHeaderRect.top}px`;
      addFieldHighlight.style.right = `${addFieldHeaderRect.right - visibleRight}px`;
      addFieldHighlight.style.bottom = `${
        addFieldHeaderRect.bottom - headerRect.bottom
      }px`;
      addFieldHighlight.style.left = `${
        addFieldButtonRect.left - addFieldHeaderRect.left
      }px`;
      addFieldHighlight.style.borderRadius = `0 ${topRightRadius} 0 0`;
      const gradientLeft = headerRect.left - borderLeftWidth;
      const gradientTop = headerRect.top - borderTopWidth;
      const gradientRight = headerRect.right - rightInset;
      const gradientBottom = headerRect.bottom + HIGHLIGHT_BORDER_WIDTH_PX;
      addFieldHighlight.style.backgroundRepeat = 'no-repeat';
      addFieldHighlight.style.backgroundSize = `${Math.max(
        0,
        gradientRight - gradientLeft
      )}px ${Math.max(0, gradientBottom - gradientTop)}px`;
      addFieldHighlight.style.backgroundPosition = `${
        gradientLeft - addFieldButtonRect.left
      }px ${gradientTop - headerRect.top}px`;
      addFieldHighlight.style.clipPath =
        `polygon(0 0, 100% 0, 100% 100%, ` +
        `calc(100% - ${HIGHLIGHT_BORDER_WIDTH_PX}px) 100%, ` +
        `calc(100% - ${HIGHLIGHT_BORDER_WIDTH_PX}px) ${topRightRadius}, ` +
        `calc(100% - ${topRightRadius}) ${topRightRadius}, ` +
        `calc(100% - ${topRightRadius}) ${HIGHLIGHT_BORDER_WIDTH_PX}px, ` +
        `0 ${HIGHLIGHT_BORDER_WIDTH_PX}px)`;
    }
  };
  updateBounds();

  return {
    updateBounds,
    remove: () => {
      headerHighlight.remove();
      addFieldHighlight?.remove();
      delete header.dataset.aiSummaryColumnStreaming;
      header.style.borderBottom = previousStyles.borderBottom;
      header.style.boxShadow = previousStyles.boxShadow;
    }
  };
};

export const useAiSummaryColumnStreamingHighlight = ({
  fieldIds,
  streamingFieldId
}: UseAiSummaryColumnStreamingHighlightOptions): void => {
  const isAiSummaryFieldEnabled = useIsAiSummaryFieldEnabled();
  const fieldOrderKey = fieldIds.join('\u0000');
  const isTargetLastVisibleField = streamingFieldId === fieldIds[fieldIds.length - 1];

  useEffect(() => {
    if (!isAiSummaryFieldEnabled || !streamingFieldId) {
      return;
    }

    const controller = getService('TableHighlightsController');
    const container = controller.getScrollContainer();
    const header = container ? findFieldHeader(container, streamingFieldId) : null;
    const table = container?.querySelector<HTMLTableElement>(`#${TABLE_ID}`) ?? null;
    const bottomControls =
      container?.querySelector<HTMLElement>(`#${BOTTOM_CONTROLS_ID}`) ?? null;
    const columnIndex = Number.parseInt(header?.dataset.colIndex ?? '', 10);

    if (!container || !table || !header || !Number.isFinite(columnIndex)) {
      return;
    }

    const layer = controller.createLayer();
    const { handle, range } = addColumnHighlight(layer, columnIndex);
    let highlight: HTMLElement | null = null;

    // Column geometry intentionally starts below the sticky header.
    const headerHighlight = applyHeaderHighlight(
      container,
      header,
      isTargetLastVisibleField
    );
    const applyClip = () => {
      if (highlight) {
        applyColumnViewportClip(container, table, header, highlight, bottomControls);
      }
    };
    const unbindHighlight = handle.bindElements(({ element, role }) => {
      if (role !== 'primary') {
        return;
      }

      highlight = element;
      element.style.boxSizing = 'border-box';
      element.style.border = '2px solid transparent';
      element.style.borderTop = '0';
      element.style.borderImage = GRADIENT_BORDER;
      applyClip();

      return () => {
        element.style.clipPath = '';

        if (highlight === element) {
          highlight = null;
        }
      };
    });

    let lastScrollLeft = container.scrollLeft;
    const handleScroll = () => {
      if (container.scrollLeft !== lastScrollLeft) {
        lastScrollLeft = container.scrollLeft;
        headerHighlight.updateBounds();
      }
      applyClip();
    };
    container.addEventListener('scroll', handleScroll, { passive: true });

    let redrawFrame: number | null = null;
    const scheduleRedraw = () => {
      if (redrawFrame !== null) {
        return;
      }
      redrawFrame = requestAnimationFrame(() => {
        redrawFrame = null;
        controller.invalidateGeometry();
        layer.update(range, 'column');
        headerHighlight.updateBounds();
        applyClip();
      });
    };

    let animationFrame = 0;
    let animationStartTime: number | undefined;
    const animate = (timestamp: number) => {
      animationStartTime ??= timestamp;
      const angle = ((timestamp - animationStartTime) * GRADIENT_DEGREES_PER_MS) % 360;
      container.style.setProperty('--database-ai-summary-gradient-angle', `${angle}deg`);
      animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);

    document.addEventListener(COLUMN_RESIZE_EVENT_NAME, scheduleRedraw);

    const resizeObserver = new ResizeObserver(scheduleRedraw);
    resizeObserver.observe(container);
    resizeObserver.observe(table);
    resizeObserver.observe(header);
    if (bottomControls) {
      resizeObserver.observe(bottomControls);
    }

    return () => {
      cancelAnimationFrame(animationFrame);
      if (redrawFrame !== null) {
        cancelAnimationFrame(redrawFrame);
      }
      resizeObserver.disconnect();
      container.removeEventListener('scroll', handleScroll);
      document.removeEventListener(COLUMN_RESIZE_EVENT_NAME, scheduleRedraw);
      headerHighlight.remove();
      unbindHighlight();
      container.style.removeProperty('--database-ai-summary-gradient-angle');
      layer.dispose();
    };
  }, [
    fieldOrderKey,
    isAiSummaryFieldEnabled,
    isTargetLastVisibleField,
    streamingFieldId
  ]);
};
